import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getAllocatedFrames,
  getAssetsDictionary,
  getMissingAssets,
  getUnavailableAssets,
  getSelectedDistributionAssets,
} from 'store/dealManagement/selectors/availabilityAssets';
import { DirectSalesAsset } from 'components/common/types/DirectSalesCampaign.types';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { Store } from 'components/common/types/Store.types';
import { getIsSharedCampaign, getSharedCampaignPlanAssets } from 'store/sharedCampaignPlan/selectors';

export const usePlannerMapAssets = (): DirectSalesAsset[] => {
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  const allocatedFrames = useSelector(getAllocatedFrames);
  const visibleAssets = useSelector(getSelectedDistributionAssets);
  const unavailableAssets = useSelector(getUnavailableAssets);
  const missingAssets = useSelector(getMissingAssets);
  const isSharedCampaign = useSelector(getIsSharedCampaign);
  const sharedCampaignAssets = useSelector(getSharedCampaignPlanAssets);
  const assetsView = useSelector((state: Store) => state.dealManagement.plannerSettings.assetsView);
  const assetsDictionary = useSelector(getAssetsDictionary);

  const routeFrameCodes =
    useSelector((state: Store) =>
      isDirectSalesCampaignType
        ? state.dealManagement.commonDeal.currentLine.mapRouteFrameCodes
        : state.dealManagement.commonDeal.currentLine.routeFrameCodes,
    ) ?? [];

  return useMemo(() => {
    if (isSharedCampaign) {
      return sharedCampaignAssets;
    }

    if (allocatedFrames || assetsView === 'AVAILABLE') return [...visibleAssets, ...missingAssets];

    if (assetsView === 'SELECTED') {
      return routeFrameCodes.reduce((acc, frame) => {
        if (!frame.include) return acc;

        const asset = assetsDictionary.get(frame.code);

        if (asset) acc.push(asset);
        return acc;
      }, [] as DirectSalesAsset[]);
    }

    return [...visibleAssets, ...unavailableAssets, ...missingAssets];
  }, [
    assetsDictionary,
    allocatedFrames,
    routeFrameCodes,
    assetsView,
    isSharedCampaign,
    sharedCampaignAssets,
    visibleAssets,
    unavailableAssets,
    missingAssets,
  ]);
};
